import { hslToRgb, rgbToHsl, type RGBA } from '../lib/color';

export interface SlidersRef {
  root: HTMLElement;
  update: (c: RGBA) => void;
  onChange: (fn: (c: RGBA) => void) => void;
}

interface ChannelDef {
  key: 'h' | 's' | 'l' | 'a';
  labelKey: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
}

const CHANNELS: ChannelDef[] = [
  { key: 'h', labelKey: 'H', min: 0, max: 360, step: 1, unit: '°' },
  { key: 's', labelKey: 'S', min: 0, max: 100, step: 1, unit: '%' },
  { key: 'l', labelKey: 'L', min: 0, max: 100, step: 1, unit: '%' },
  { key: 'a', labelKey: 'A', min: 0, max: 1, step: 0.01 }
];

export function createSliders(initial: RGBA): SlidersRef {
  const root = document.createElement('section');
  root.className = 'sliders';

  const inputs: Record<string, HTMLInputElement> = {};
  const valueEls: Record<string, HTMLSpanElement> = {};
  const tracks: Record<string, HTMLDivElement> = {};

  let changeFn: ((c: RGBA) => void) | null = null;
  let currentColor: RGBA = initial;

  for (const ch of CHANNELS) {
    const row = document.createElement('div');
    row.className = 'sliders__row';

    const label = document.createElement('label');
    label.className = 'sliders__label';
    label.textContent = ch.labelKey;

    const track = document.createElement('div');
    track.className = 'sliders__track';

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(ch.min);
    input.max = String(ch.max);
    input.step = String(ch.step);
    input.className = 'sliders__input';
    input.setAttribute('aria-label', ch.labelKey);
    inputs[ch.key] = input;
    tracks[ch.key] = track;

    const value = document.createElement('span');
    value.className = 'sliders__value';
    valueEls[ch.key] = value;

    const updateValue = (raw: number) => {
      if (ch.key === 'a') {
        value.textContent = `${Math.round(raw * 100)}%`;
      } else {
        value.textContent = `${Math.round(raw)}${ch.unit ?? ''}`;
      }
    };

    input.addEventListener('input', () => {
      const raw = parseFloat(input.value);
      updateValue(raw);
      const hsl = {
        h: parseFloat(inputs.h.value),
        s: parseFloat(inputs.s.value),
        l: parseFloat(inputs.l.value),
        a: parseFloat(inputs.a.value)
      };
      const c = hslToRgb(hsl);
      currentColor = c;
      updateGradient();
      if (changeFn) changeFn(c);
    });

    track.appendChild(input);
    row.append(label, track, value);
    root.appendChild(row);
  }

  function buildGradient(): void {
    const hsl = rgbToHsl(currentColor);
    const { h, s, l } = hsl;

    // Hue: full rainbow gradient
    tracks.h.style.background =
      `linear-gradient(to right, hsl(0,${s}%,${l}%), hsl(60,${s}%,${l}%), hsl(120,${s}%,${l}%), hsl(180,${s}%,${l}%), hsl(240,${s}%,${l}%), hsl(300,${s}%,${l}%), hsl(360,${s}%,${l}%))`;

    // Saturation: gray to full hue color
    tracks.s.style.background =
      `linear-gradient(to right, hsl(${h},0%,${l}%), hsl(${h},100%,${l}%))`;

    // Lightness: black to current hue to white
    tracks.l.style.background =
      `linear-gradient(to right, hsl(${h},${s}%,0%), hsl(${h},${s}%,50%), hsl(${h},${s}%,100%))`;

    // Alpha: transparent to current color
    tracks.a.style.background =
      `linear-gradient(to right, transparent, hsl(${h},${s}%,${l}%))`;
  }

  function updateGradient(): void {
    buildGradient();
  }

  function update(c: RGBA): void {
    currentColor = c;
    const hsl = rgbToHsl(c);
    inputs.h.value = String(hsl.h);
    inputs.s.value = String(hsl.s);
    inputs.l.value = String(hsl.l);
    inputs.a.value = String(c.a);
    for (const ch of CHANNELS) {
      const raw = parseFloat(inputs[ch.key].value);
      if (ch.key === 'a') {
        valueEls[ch.key].textContent = `${Math.round(raw * 100)}%`;
      } else {
        valueEls[ch.key].textContent = `${Math.round(raw)}${ch.unit ?? ''}`;
      }
    }
    updateGradient();
  }

  function onChange(fn: (c: RGBA) => void): void {
    changeFn = fn;
  }

  update(initial);

  return { root, update, onChange };
}
